import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { catchError, map } from 'rxjs/operators';
import { AppState, fromAppActions } from '../shared/state';
import { WindowService } from '../shared/services/window/window.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { fromAuthenticationActions } from '../shared/state/actions/authentication/authentication.actions';
import { AuthenticationGuard } from '../shared/guards';

// Drupal's post-login ?tk= redirect always targets this exact path, for every user, pro or
// standard — never bare /inbox. It must be treated as the generic landing page (forwarded to
// SupplierDefaultPageGuard so pro users get swapped onto the pipeline) rather than preserved
// as if it were a genuine deep link the user was trying to reach before being logged out.
const DEFAULT_LOGIN_LANDING_SEGMENTS = ['inbox', 'en', 'requests', 'live'];

@Injectable()
export class AuthenticationWrapperGuard {
  constructor(
    @Inject('environment') private environment,
    private _authenticationGuard: AuthenticationGuard,
    private _authService: AuthService,
    private _router: Router,
    private _store: Store<AppState>,
    private _windowService: WindowService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    // TO DO: Remove route.queryParamMap.get('token')
    const token = route.queryParamMap.get('tk') || route.queryParamMap.get('token');

    if (token) {
      this._store.dispatch(new fromAppActions.RemoveListing());
      this._store.dispatch(new fromAuthenticationActions.RemoveAuth());

      return this._authService.signInWithCustomToken$(token).pipe(
        map(() => {
          const listingUuid = route.paramMap.get('listingUuid');
          const childPath = route.firstChild?.url?.[0]?.path;
          // Only redirect bare /inbox — a firstChild.firstChild means the URL has a deeper
          // sub-route (e.g. /inbox/en/details/.../new-quote/page) that must be preserved.
          const isBareInbox = childPath === 'inbox' && !route.firstChild?.firstChild;
          const isDefaultLoginLanding = this._matchesExactPath(route, DEFAULT_LOGIN_LANDING_SEGMENTS);

          if (!childPath || isBareInbox || isDefaultLoginLanding) {
            return this._router.createUrlTree(['/su', listingUuid]);
          }

          return true;
        }),
        catchError(() => {
          this._windowService.open(this.environment.loginRoute, '_self');

          return of(false);
        })
      );
    }

    return this._authenticationGuard.canActivate();
  }

  /** Walks route.firstChild descendants, checking each segment matches in order with nothing deeper. */
  private _matchesExactPath(route: ActivatedRouteSnapshot, segments: string[]): boolean {
    let current = route.firstChild;

    for (const segment of segments) {
      if (current?.url?.[0]?.path !== segment) {
        return false;
      }

      current = current.firstChild;
    }

    return !current;
  }
}
